import { authedFetch } from "#utils/apiFetch"

class GptHandlerInstance {
  private async post<T>(
    endpoint: string,
    body: Record<string, unknown>
  ): Promise<T> {
    const response = await authedFetch(`/api/gpt/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })

    const raw = await response.text()
    let parsed: unknown = null
    if (raw.trim()) {
      try {
        parsed = JSON.parse(raw.replace(/^\uFEFF/, ""))
      } catch {
        throw new Error(
          `GPT request returned ${response.status} with a non-JSON body`
        )
      }
    }

    if (!response.ok) {
      const serverMessage =
        parsed &&
        typeof parsed === "object" &&
        "error" in parsed &&
        typeof parsed.error === "string"
          ? parsed.error
          : ""
      throw new Error(
        serverMessage || `GPT request failed (${response.status})`
      )
    }

    if (!parsed || typeof parsed !== "object") {
      throw new Error(
        `GPT request returned ${response.status} with an empty body`
      )
    }

    return parsed as T
  }

  async generateText(prompt: string): Promise<string | undefined> {
    const { result } = await this.post<{ result: string }>("generateText", {
      prompt,
    })
    return result
  }

  /**
   * Turns a standout moment's keywords into one or two parent-shareable
   * sentences. The prompt lives server-side (standout-moments convention);
   * the emulator stack answers with mock lorem ipsum.
   */
  async generateStandoutNote(params: {
    studentFirstName: string
    category: string
    tagTexts: string[]
    note?: string
  }): Promise<string | undefined> {
    const body = await this.post<{ result?: unknown }>(
      "generateStandoutNote",
      params
    )
    return typeof body.result === "string" ? body.result : undefined
  }

  /**
   * Advisory auto-approve score for a teacher's quick story. The rubric
   * lives on the server. The caller stores the result on the moment.
   */
  async scoreQuickStory(params: { note: string }): Promise<{
    confidence: number
    positive: boolean
    appropriate: boolean
    reason: string
    source: "model" | "stand-in"
  }> {
    const body = await this.post<Record<string, unknown>>(
      "scoreQuickStory",
      params
    )
    const confidence = body.confidence
    const reason = body.reason
    if (
      typeof confidence !== "number" ||
      !Number.isFinite(confidence) ||
      typeof body.positive !== "boolean" ||
      typeof body.appropriate !== "boolean" ||
      typeof reason !== "string" ||
      reason.trim().length === 0 ||
      (body.source !== "model" && body.source !== "stand-in")
    ) {
      throw new Error("Quick story score came back in an unexpected shape")
    }
    return {
      confidence,
      positive: body.positive,
      appropriate: body.appropriate,
      reason: reason.trim(),
      source: body.source,
    }
  }

  /**
   * Which of one student's family notes are too similar to another of
   * them. The rubric and the model live on the server. The caller stores
   * the result.
   */
  async judgeFamilyRepeat(params: {
    messages: Array<{ id: string; text: string }>
  }): Promise<{
    messages: Array<{
      id: string
      repeats: boolean
      similarIds: string[]
      reason: string
    }>
    source: "model" | "stand-in"
  }> {
    const body = await this.post<Record<string, unknown>>(
      "judgeFamilyRepeat",
      params
    )
    if (!Array.isArray(body.messages)) {
      throw new Error("Family repeat judgment came back in an unexpected shape")
    }
    if (body.source !== "model" && body.source !== "stand-in") {
      throw new Error("Family repeat judgment came back in an unexpected shape")
    }
    const messages = body.messages.map((row) => {
      if (!row || typeof row !== "object") {
        throw new Error(
          "Family repeat judgment came back in an unexpected shape"
        )
      }
      const record = row as Record<string, unknown>
      const similarIds = record.similarIds
      if (
        typeof record.id !== "string" ||
        typeof record.repeats !== "boolean" ||
        typeof record.reason !== "string" ||
        record.reason.trim().length === 0 ||
        !Array.isArray(similarIds) ||
        similarIds.some((id) => typeof id !== "string")
      ) {
        throw new Error(
          "Family repeat judgment came back in an unexpected shape"
        )
      }
      return {
        id: record.id,
        repeats: record.repeats,
        similarIds,
        reason: record.reason.trim(),
      }
    })
    return { messages, source: body.source }
  }

  async evaluateArtwork(prompt?: string): Promise<string | undefined> {
    const defaultImage =
      "https://firebasestorage.googleapis.com/v0/b/hongyunart-55f89.appspot.com/o/-KvRr37dWKy51p3JPU2L%2F2023%20C%2F-NQCumIf4EqqQTD_kyaW%2F2023-11-16%2007%3A41%3A59%20Lily%20Han%201011.jpg?alt=media&token=ea634fbc-c8d9-4f55-aaed-b199ea71cde1"

    const { result } = await this.post<{ result: string }>("evaluateArtwork", {
      prompt: prompt ?? "",
      imageUrl: defaultImage,
    })
    return result
  }

  async streamTagFeedback(
    category: string,
    tags: string[],
    context: {
      age: number
      classGroup: string
      recentMoments?: string[]
      persona?: string
      writingStyle?: string
      studentFirstName?: string
      traits?: string[]
    },
    callbacks: {
      onDelta: (content: string) => void
      onDone: () => void
      onError: (error: string) => void
    }
  ): Promise<void> {
    const response = await authedFetch("/api/gpt/streamTagFeedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category, tags, ...context }),
    })

    if (!response.ok || !response.body) {
      const errorBody = await response.json().catch(() => ({}))
      callbacks.onError(
        (errorBody as { error?: string }).error ?? "Stream request failed"
      )
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split("\n")
      buffer = lines.pop()!

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue
        const event = JSON.parse(line.slice(6)) as
          | { type: "delta"; field: string; content: string }
          | { type: "done" }
          | { type: "error"; message: string }

        switch (event.type) {
          case "delta":
            callbacks.onDelta(event.content)
            break
          case "done":
            callbacks.onDone()
            return
          case "error":
            callbacks.onError(event.message)
            return
        }
      }
    }

    callbacks.onDone()
  }
}

export const GptHandler = new GptHandlerInstance()
